import { toCsv } from "../shared/csv";

export type DefaulterRow = {
  studentId: string;
  name: string;
  admissionNo: number;
  itsId: string;
  className: string;
  guardianName: string | null;
  guardianPhone: string | null;
  overdueCount: number;
  overdueAmount: number;
  oldestDueDate: string | null;
  totalOutstanding: number;
};

/** CSV of the defaulters list, one row per student, ready for follow-up calls. */
export function defaultersToCsv(rows: readonly DefaulterRow[], asOf: string): string {
  return toCsv([
    [
      "Adm. no",
      "Student",
      "ITS",
      "Class",
      "Guardian",
      "Guardian WhatsApp",
      `Overdue as of ${asOf} (Rs)`,
      "Overdue dues",
      "Oldest due date",
      "Total outstanding (Rs)",
    ],
    ...rows.map((r) => [
      String(r.admissionNo),
      r.name,
      r.itsId,
      r.className,
      r.guardianName ?? "",
      r.guardianPhone ?? "",
      String(r.overdueAmount),
      String(r.overdueCount),
      r.oldestDueDate ?? "",
      String(r.totalOutstanding),
    ]),
  ]);
}

export type Mode = "CASH" | "BANK_TRANSFER" | "CHEQUE" | "UPI";
export const MODE_ORDER: Mode[] = ["CASH", "BANK_TRANSFER", "CHEQUE", "UPI"];
export const MODE_LABEL: Record<Mode, string> = {
  CASH: "Cash",
  BANK_TRANSFER: "Bank transfer",
  CHEQUE: "Cheque",
  UPI: "UPI",
};

export type CollectionSummary = {
  dateFrom: string;
  dateTo: string;
  totalCollected: number;
  receiptCount: number;
  voidedAmount: number;
  voidedCount: number;
  byMode: Record<Mode, number>;
  byClass: Array<{
    classId: string;
    className: string;
    byMode: Record<Mode, number>;
    total: number;
  }>;
  byFeeHead: Array<{ feeHeadName: string; amount: number }>;
  gridTotal: number;
};

/** CSV of the collection summary: the class x mode grid with totals, then the fee-head split. */
export function collectionToCsv(s: CollectionSummary): string {
  const head = ["Class", ...MODE_ORDER.map((m) => MODE_LABEL[m]), "Total (Rs)"];
  const grid = s.byClass.map((c) => [
    c.className,
    ...MODE_ORDER.map((m) => String(c.byMode[m])),
    String(c.total),
  ]);
  const totals = [
    "All classes",
    ...MODE_ORDER.map((m) => String(s.byMode[m])),
    String(s.totalCollected),
  ];
  return toCsv([
    [`Fees collected ${s.dateFrom} to ${s.dateTo}`],
    head,
    ...grid,
    totals,
    [],
    ["Fee", "Amount (Rs)"],
    ...s.byFeeHead.map((f) => [f.feeHeadName, String(f.amount)]),
    [],
    ["Valid receipts", String(s.receiptCount)],
    ["Voided receipts (not counted)", String(s.voidedCount)],
  ]);
}

/** Default range for the summary: the academic year's start (never after today) up to today. */
export function defaultSummaryRange(
  year: { startDate: string; endDate: string } | undefined,
  today: string
): { dateFrom: string; dateTo: string } {
  if (!year) return { dateFrom: `${today.slice(0, 8)}01`, dateTo: today };
  const from = year.startDate > today ? today : year.startDate;
  const to = today > year.endDate ? year.endDate : today;
  return { dateFrom: from <= to ? from : to, dateTo: to };
}

/** True when the class x mode grid and the headline total disagree (should never happen). */
export const summaryIsInconsistent = (
  s: Pick<CollectionSummary, "gridTotal" | "totalCollected">
): boolean => s.gridTotal !== s.totalCollected;
